import { cn } from "@-/com.npmjs.cn";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { useIsMorphemeInfoPanelSidebarCollapsed } from "#use-is-morpheme-info-panel-sidebar-collapsed";
import { ToolbarActionsLane } from "@-/dev.improve.haniverse.components.ui.toolbar.button.icon";
import {
  ToolbarPillRow,
  ToolbarViewButton,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  PrimaryControlsStripBackControl,
  usePrimaryControlsStripBackGate,
} from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { PrimaryControlsStripToolbar } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import {
  Pressable,
  ScrollView,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "@-/com.npmjs.react-native";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

export interface MorphemeInfoPanelSidebarGroup {
  key: string;
  sections: MorphemeInfoPanelAccordionSectionDescriptor[];
  title: string;
}

const HEADER_KEY = "__header";
const HEADER_TITLE = "Meaning";

/**
 * A section counts as "reached" once its top is within this many px of the scroll's top edge, so
 * the highlight flips as the heading arrives rather than only once it is flush with the edge.
 */
const SCROLL_SPY_SLOP = 24;
/** Longest an animated `scrollTo` is allowed to keep the tapped key pinned before spy resumes. */
const PENDING_JUMP_TIMEOUT_MS = 800;

interface SectionNavEntry {
  key: string;
  title: string;
}

/**
 * One coordinate space for every section: offsets recorded from `onLayout` on the DIRECT children
 * of the content scroll, imperative `scrollTo` for jumps, and scroll-spy that keeps `activeKey` on
 * the section currently under the top edge. A tapped key stays pinned while its animated jump is
 * in flight (the spy would otherwise flicker through every section it scrolls past, and could land
 * on the previous section when the target is too close to the end to reach the top edge).
 */
function useSectionScrollSpy(entries: readonly SectionNavEntry[]) {
  const scrollRef = useRef<ScrollView | null>(null);
  const offsetMapRef = useRef(new Map<string, number>());
  const pendingJumpRef = useRef<{ key: string; timeout: ReturnType<typeof setTimeout> } | null>(
    null,
  );
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const handleLayout = useCallback((key: string, event: LayoutChangeEvent) => {
    offsetMapRef.current.set(key, event.nativeEvent.layout.y);
  }, []);

  const clearPendingJump = useCallback(() => {
    if (pendingJumpRef.current !== null) {
      clearTimeout(pendingJumpRef.current.timeout);
      pendingJumpRef.current = null;
    }
  }, []);

  useEffect(() => clearPendingJump, [clearPendingJump]);

  const scrollToKey = useCallback(
    (key: string) => {
      const y = offsetMapRef.current.get(key);
      if (y === undefined) {
        return;
      }
      clearPendingJump();
      pendingJumpRef.current = {
        key,
        timeout: setTimeout(() => {
          pendingJumpRef.current = null;
        }, PENDING_JUMP_TIMEOUT_MS),
      };
      setActiveKey(key);
      scrollRef.current?.scrollTo({ animated: true, y });
    },
    [clearPendingJump],
  );

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const scrollY = event.nativeEvent.contentOffset.y + SCROLL_SPY_SLOP;
      let reachedKey: string | null = null;
      for (const entry of entries) {
        const y = offsetMapRef.current.get(entry.key);
        if (y !== undefined && y <= scrollY) {
          reachedKey = entry.key;
        }
      }
      const pending = pendingJumpRef.current;
      if (pending !== null) {
        if (reachedKey === pending.key) {
          clearPendingJump();
        }
        return;
      }
      setActiveKey(reachedKey);
    },
    [clearPendingJump, entries],
  );

  return { activeKey, clearPendingJump, handleLayout, handleScroll, scrollRef, scrollToKey };
}

function getNavEntries(
  groups: readonly MorphemeInfoPanelSidebarGroup[],
  hasHeader: boolean,
): SectionNavEntry[] {
  const entries: SectionNavEntry[] = hasHeader ? [{ key: HEADER_KEY, title: HEADER_TITLE }] : [];
  for (const group of groups) {
    for (const section of group.sections) {
      entries.push({ key: section.headerKey, title: section.title });
    }
  }
  return entries;
}

/**
 * The Reference page: section navigation plus one continuous scroll with sections grouped under
 * the tab heading they came from.
 *
 * Desktop web gets a section rail on the left. Collapsible sections are a small-screen affordance
 * that keeps a phone's single column navigable; at desktop width they turn the character page into
 * a column of mostly-closed bars, and the sites this page is modelled on (HanziHero, Kanshudo,
 * Hanzi-Trainer) present everything at once with a nav rail.
 *
 * Native, and web narrower than the rail breakpoint (see
 * `useIsMorphemeInfoPanelSidebarCollapsed`), never show that rail: it would eat 240px
 * of a column that is already a phone wide. There the same navigation collapses into a
 * horizontally scrolling pill row pinned above the content, which follows the scroll (scroll-spy)
 * and keeps the active pill in view. Both layouts share one scroll and one set of jump targets.
 *
 * Every body renders EXPANDED and the navigation only scrolls — it is never a view switcher, so
 * the whole page stays readable top to bottom. The descriptors' `isOpen`/`onToggle` are
 * deliberately ignored here (they still drive the native accordion panels): every section hook
 * defaults `isOpen` to a local `useState` and never persists it, so nothing is lost.
 *
 * This owns the page's full height (`flex-1` over the viewport) and must NOT be nested inside
 * another ScrollView — a `flex-1` child of a scroll container has no bounded height and collapses
 * to zero, which silently blanks every section.
 */
export function MorphemeInfoPanelGroupedSidebar({
  groups,
  header,
}: {
  groups: MorphemeInfoPanelSidebarGroup[];
  // Rendered above the first group inside the scroll — the Reference page's Meaning/Pronunciation
  // content, which comes from the mnemonics package rather than an info section descriptor.
  header?: ReactNode;
}) {
  const isCollapsed = useIsMorphemeInfoPanelSidebarCollapsed();
  const hasHeader = header !== undefined;
  const entries = useMemo(() => getNavEntries(groups, hasHeader), [groups, hasHeader]);
  const { activeKey, clearPendingJump, handleLayout, handleScroll, scrollRef, scrollToKey } =
    useSectionScrollSpy(entries);

  const content = (
    <ScrollView
      className="flex-1"
      contentContainerClassName="pb-16"
      onScroll={handleScroll}
      onScrollBeginDrag={clearPendingJump}
      ref={scrollRef}
      scrollEventThrottle={16}
    >
      {hasHeader && <View onLayout={(event) => handleLayout(HEADER_KEY, event)}>{header}</View>}
      {/* Flattened deliberately: `onLayout.y` is relative to the DIRECT parent, so wrapping each
          group in its own View would make every section report an offset inside that group
          rather than inside the scroll — and `scrollTo` would land in the wrong place. Keeping
          every section as a direct child puts all offsets in one coordinate space. */}
      {groups.flatMap((group) =>
        group.sections.map((section) => (
          <View
            key={`${group.key}:${section.headerKey}`}
            onLayout={(event) => handleLayout(section.headerKey, event)}
          >
            <View className="flex-row items-center gap-2 border-b border-border bg-surface px-4 py-3">
              <Icon className="text-muted-foreground" name={section.iconName} size={16} />
              <Text className="flex-1 text-base font-semibold text-foreground">
                {section.title}
              </Text>
              {/* Bodies never collapse here, but a section with its own trailing toggle (Compounds'
                  compact/detailed switch) still needs it. */}
              {section.headerActions?.map((action) => (
                <Pressable
                  accessibilityLabel={action.label}
                  accessibilityRole="button"
                  accessibilityState={{ selected: action.selected }}
                  hitSlop={8}
                  key={action.label}
                  onPress={action.onPress}
                >
                  <Icon
                    className={action.selected ? "text-primary" : "text-muted-foreground"}
                    name={action.iconName}
                    size={18}
                  />
                </Pressable>
              ))}
              {section.trailingIconName !== undefined && (
                <Pressable
                  accessibilityLabel={`Toggle ${section.title} view`}
                  accessibilityRole="button"
                  hitSlop={8}
                  onPress={section.onToggle}
                >
                  <Icon className="text-foreground" name={section.trailingIconName} size={18} />
                </Pressable>
              )}
            </View>
            <View className="border-b border-border bg-surface">{section.renderBody()}</View>
          </View>
        )),
      )}
    </ScrollView>
  );

  if (isCollapsed) {
    return (
      <View className="flex-1 bg-muted">
        <MorphemeInfoPanelSectionPillRail
          activeKey={activeKey}
          entries={entries}
          onSelect={scrollToKey}
        />
        {content}
      </View>
    );
  }

  return (
    <View className="flex-1 flex-row bg-muted">
      <View className="w-60 shrink-0 border-r border-border bg-surface">
        <ScrollView contentContainerClassName="py-4">
          {hasHeader && (
            <Pressable
              accessibilityLabel={`Jump to ${HEADER_TITLE}`}
              accessibilityRole="link"
              className={cn("px-4 py-2", activeKey === HEADER_KEY && "bg-accent")}
              onPress={() => scrollToKey(HEADER_KEY)}
            >
              <Text className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {HEADER_TITLE}
              </Text>
            </Pressable>
          )}
          {groups.map((group) => (
            <View className="pt-3" key={group.key}>
              <Text className="px-4 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                {group.title}
              </Text>
              {group.sections.map((section) => (
                <Pressable
                  accessibilityLabel={`Jump to ${section.title}`}
                  accessibilityRole="link"
                  className={cn(
                    "flex-row items-center gap-2 px-4 py-1.5",
                    activeKey === section.headerKey && "bg-accent",
                  )}
                  key={section.headerKey}
                  onPress={() => scrollToKey(section.headerKey)}
                >
                  <Icon className="text-muted-foreground" name={section.iconName} size={13} />
                  <Text className="text-sm text-foreground">{section.title}</Text>
                </Pressable>
              ))}
            </View>
          ))}
        </ScrollView>
      </View>
      {content}
    </View>
  );
}

/**
 * The collapsed navigation: the screen's toolbar row IS the section navigation — one horizontally
 * scrolling row of the app's standard pills, built from the same primitives as the Browse toolbars.
 * `PrimaryControlsStripToolbar` publishes the row's height so the shell's header fade starts under it,
 * `ToolbarPillRow` keeps the pills out of the shell's absolute profile-trigger lane (and, when a
 * back control exists, out of its leading lane), and the active pill is scrolled into view whenever
 * it changes so following the page (scroll-spy) or tapping near an edge never leaves the highlight
 * off-screen.
 */
function MorphemeInfoPanelSectionPillRail({
  activeKey,
  entries,
  onSelect,
}: {
  activeKey: string | null;
  entries: readonly SectionNavEntry[];
  onSelect: (key: string) => void;
}) {
  const railRef = useRef<ScrollView | null>(null);
  const railWidthRef = useRef(0);
  const pillFramesRef = useRef(new Map<string, { width: number; x: number }>());
  const backGate = usePrimaryControlsStripBackGate({ appShellPlacement: "toolbar" });

  const handleRailLayout = useCallback((event: LayoutChangeEvent) => {
    railWidthRef.current = event.nativeEvent.layout.width;
  }, []);

  useEffect(() => {
    if (activeKey === null) {
      return;
    }
    const frame = pillFramesRef.current.get(activeKey);
    const railWidth = railWidthRef.current;
    if (frame === undefined || railWidth === 0) {
      return;
    }
    railRef.current?.scrollTo({
      animated: true,
      x: Math.max(0, frame.x + frame.width / 2 - railWidth / 2),
    });
  }, [activeKey]);

  return (
    <PrimaryControlsStripToolbar className="relative" pointerEvents="box-none">
      <ToolbarPillRow
        contentContainerClassName="p-1"
        hasLeadingActions={backGate !== null}
        onLayout={handleRailLayout}
        ref={railRef}
      >
        {entries.map((entry) => (
          <View
            key={entry.key}
            onLayout={(event) => {
              const { width, x } = event.nativeEvent.layout;
              pillFramesRef.current.set(entry.key, { width, x });
            }}
          >
            <ToolbarViewButton
              accessibilityLabel={`Jump to ${entry.title}`}
              label={entry.title}
              onPress={() => onSelect(entry.key)}
              selected={entry.key === activeKey}
            />
          </View>
        ))}
      </ToolbarPillRow>
      {backGate !== null && (
        <ToolbarActionsLane>
          <PrimaryControlsStripBackControl gate={backGate} layout="app-shell" />
        </ToolbarActionsLane>
      )}
    </PrimaryControlsStripToolbar>
  );
}
